import React, { useEffect, useMemo, useRef, useState } from "react";
import "./draftEmail.css";
import { Box, Typography, CircularProgress } from "@mui/material";
import {
    Mail,
    X,
    Search,
    Trash2,
    Send,
    CheckCircle2,
    Inbox,
    ArrowUpRight,
    ArrowDownLeft,
    PenLine,
    Reply,
    ChevronLeft,
    ChevronRight,
    Pen,
    Expand,
} from "lucide-react";
import {
    emailTriagePostMailAction,
    emailTriageDeleteMail,
    emailTriagePostProcessMailV2,
} from "./emailTriageApi";
import {
    formatEmailTimestampToIST,
    normalizeRecipientList,
    resolveEmailBodyFromApi,
    getEmailRecordId,
    mergeEmailMailSourceRecords,
    buildMailActionPayload,
    normalizeMailActionSourceRecord,
    buildProcessMailV2ItemsSnapshot,
    buildProcessMailV2ItemsFromRows,
    getUserEmailFromStorage,
    getUserNameFromStorage,
} from "./emailTriageUtils";
import EmailDraftUI from "Agentic Module/components/draftEmailSubComponents/emailDraftUI";
import EmailSentUI from "Agentic Module/components/draftEmailSubComponents/emailSentUI";
import EmailInboxUI from "Agentic Module/components/draftEmailSubComponents/emailInboxUI";
import DeleteConfirmationModel from "Agentic Module/components/common/deleteConfirmationModel";
import PropTypes from "prop-types";

function historyListSubject(email) {
    return (
        email?.email_sub ??
        email?.mail_subject ??
        "(No subject)"
    );
}

function filterEmailsBySearch(emails, searchQuery) {
    const q = (searchQuery || "").trim().toLowerCase();
    if (!q) return emails;
    return emails.filter((email) => {
        const to = Array.isArray(email.email_to) ? email.email_to.join(", ") : (email.email_to || "");
        const cc = Array.isArray(email.email_cc) ? email.email_cc.join(", ") : (email.email_cc || "");
        const haystack = `${email.email_sub || email.mail_subject || ""} ${email.email_from || ""} ${to} ${cc} ${email.email_body || email.mail_content || ""} ${email.timestamp || ""}`.toLowerCase();
        return haystack.includes(q);
    });
}

function DraftEmailApiLoader({ active }) {
    if (!active) return null;
    return (
        <Box
            sx={{
                position: "fixed",
                inset: 0,
                zIndex: 10000000,
                background: "rgba(51, 51, 51, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
            role="status"
            aria-live="polite"
            aria-busy="true"
        >
            <Box
                sx={{
                    px: 3,
                    py: 2,
                    borderRadius: 1,
                    background: "#fff",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1.5,
                    boxShadow: 3,
                }}
            >
                <CircularProgress size={28} />
                <Typography fontSize={16} fontWeight="bold">
                    Loading...
                </Typography>
                <Typography fontSize={12} color="#888">
                    Fetching the data from the server. Please wait.
                </Typography>
            </Box>
        </Box>
    );
}

DraftEmailApiLoader.propTypes = {
  active: PropTypes.bool,
};

export default function DraftEmail({
    emailModalOpen,
    isReplyNowCompose,
    setEmailModalOpen,
    historyDraftEmails,
    historyInboxEmails,
    historySentEmails,
    emailListTab,
    setEmailListTab,
    selectedPO,
    onRefreshEmailHistory,
    onRemoveLocalDraftEmail,
    onActionError,
    emailHistoryLoading,
    autoComposeIfNoDrafts = false,
    onReplyNowAutoComposeComplete,
    selectedDispatchRows,
    vendorGrouped,
}) {

    const tabEmails = useMemo(() => {
        if (emailListTab === "drafts") return historyDraftEmails ?? [];
        if (emailListTab === "sent") return historySentEmails ?? [];
        return historyInboxEmails ?? [];
    }, [emailListTab, historyDraftEmails, historyInboxEmails, historySentEmails]);

    const [searchQuery, setSearchQuery] = useState("");
    const [selectedEmail, setSelectedEmail] = useState({});
    const [emailModalLoading, setEmailModalLoading] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    const [emailSendSuccess, setEmailSendSuccess] = useState(false);
    const [draftSaveSuccess, setDraftSaveSuccess] = useState(false);
    const [selectedListIndex, setSelectedListIndex] = useState(null);
    const [isEmailListCollapsed, setIsEmailListCollapsed] = useState(false);
    const [inboxReplyTrigger, setInboxReplyTrigger] = useState(0);
    const [draftSyncKey, setDraftSyncKey] = useState(0);
    const [isComposeNewActive, setIsComposeNewActive] = useState(false);
    const [emailFrom] = useState("HULSamarth.Supplies1@unilever.com");
    const [emailTo, setEmailTo] = useState([]);
    const [emailCc, setEmailCc] = useState([]);
    const [emailSubject, setEmailSubject] = useState("");
    const [emailBody, setEmailBody] = useState("");
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [pendingDeleteEmail, setPendingDeleteEmail] = useState(null);
    const [isReplyMode, setIsReplyMode] = useState(false);
    const [isEmailModalExpanded, setIsEmailModalExpanded] = useState(false);

    const composerMailSourceRef = useRef(null);
    const autoComposeTriggeredRef = useRef(false);
    /** Preserves unsaved To/Cc/Subject/Body when switching away from the Drafts tab. */
    const draftTabStateRef = useRef(null);
    /** Preserves inbox selection + open reply composer when switching away from Inbox. */
    const inboxTabStateRef = useRef(null);
    const inboxUiApiRef = useRef(null);
    const prevEmailListTabRef = useRef(null);
    const prevDraftCountRef = useRef(historyDraftEmails?.length ?? 0);
    const draftCount = historyDraftEmails?.length ?? 0;
    const [inboxReplyRestore, setInboxReplyRestore] = useState(null);

    function applyComposerMailSource(data) {
        if (data && typeof data === "object") {
            composerMailSourceRef.current = normalizeMailActionSourceRecord(data);
        } else {
            composerMailSourceRef.current = null;
        }
    }

    function getComposerData() {
        return {
            emailSubject,
            emailBody,
            emailTo,
            emailCc,
        };
    }

    function buildMailPayload(isDraft, sourceEmail = null, composerDataOverride = null) {
        const composerData = composerDataOverride ?? getComposerData();
        const merged = mergeEmailMailSourceRecords(
            composerMailSourceRef.current,
            sourceEmail,
        );
        const payload = buildMailActionPayload({
            isDraft,
            emailSubject: composerData.emailSubject,
            emailBody: composerData.emailBody,
            emailTo: composerData.emailTo,
            emailCc: composerData.emailCc,
            mergedSource: merged,
        });

        const isComposeOrSavedDraft =
            isComposeNewActive || emailListTab === "drafts";

        if (isComposeOrSavedDraft) {
            payload.is_new_mail = true;
        }

        return payload;
    }

    function syncComposerFromEmail(email) {
        if (!email || typeof email !== "object") return;
        applyComposerMailSource(email);
        setSelectedEmail(email);
        setEmailTo(normalizeRecipientList(email.email_to));
        setEmailCc(normalizeRecipientList(email.email_cc));
        setEmailSubject(email.email_sub ?? email.mail_subject ?? "");
        setEmailBody(resolveEmailBodyFromApi(email));
    }

    function resetTabTrackingIfModalClosed() {
        if (emailModalOpen) return false;

        prevEmailListTabRef.current = null;
        return true;
    }

    function updateTabTracking() {
        const tabJustChanged = prevEmailListTabRef.current !== emailListTab;
        const prevDraftCount = prevDraftCountRef.current;

        prevEmailListTabRef.current = emailListTab;
        prevDraftCountRef.current = draftCount;

        return { tabJustChanged, prevDraftCount };
    }

    function restoreDraftTabStateIfAvailable() {
        const saved = draftTabStateRef.current;
        if (!saved) return false;

        setSelectedEmail(saved.selectedEmail ?? {});
        setSelectedListIndex(saved.selectedListIndex);
        setIsComposeNewActive(Boolean(saved.isComposeNewActive));
        setEmailTo(Array.isArray(saved.emailTo) ? saved.emailTo : []);
        setEmailCc(Array.isArray(saved.emailCc) ? saved.emailCc : []);
        setEmailSubject(saved.emailSubject ?? "");
        setEmailBody(saved.emailBody ?? "");
        applyComposerMailSource(saved.mailSource);

        draftTabStateRef.current = null;
        setDraftSyncKey((k) => k + 1);

        return true;
    }

    function loadFirstDraftIfNeeded(tabJustChanged, prevDraftCount) {
        if (isReplyNowCompose) return false;
        if (isComposeNewActive || draftCount === 0) return false;

        const draftsJustLoaded = prevDraftCount === 0 && draftCount > 0;
        if (!tabJustChanged && !draftsJustLoaded) return false;

        syncComposerFromEmail(historyDraftEmails[0]);
        setSelectedListIndex(0);
        setDraftSyncKey((k) => k + 1);

        return true;
    }

    function shouldClearDraftComposer(tabJustChanged, prevDraftCount) {
        return (
            draftCount === 0 &&
            !emailHistoryLoading &&
            !emailModalLoading &&
            !isComposeNewActive &&
            (tabJustChanged || prevDraftCount > 0)
        );
    }

    function handleDraftsTabState(tabJustChanged, prevDraftCount) {
        if (restoreDraftTabStateIfAvailable()) return;

        if (loadFirstDraftIfNeeded(tabJustChanged, prevDraftCount)) return;

        if (shouldClearDraftComposer(tabJustChanged, prevDraftCount)) {
            clearComposerPanel();
        }
    }

    function restoreInboxTabStateIfAvailable() {
        const savedInbox = inboxTabStateRef.current;
        if (!savedInbox) return false;

        setSelectedEmail(savedInbox.selectedEmail ?? {});
        setSelectedListIndex(savedInbox.selectedListIndex);
        setIsReplyMode(Boolean(savedInbox.isReplyMode));

        if (savedInbox.isReplyMode) {
            setInboxReplyRestore({
                replyComposerEmail: savedInbox.replyComposerEmail ?? {},
                isReplyContextExpanded: Boolean(savedInbox.isReplyContextExpanded),
            });
        } else {
            setInboxReplyRestore(null);
        }

        inboxTabStateRef.current = null;
        return true;
    }

    function selectFirstTabEmailOrClear() {
        if (tabEmails.length > 0) {
            setSelectedEmail(tabEmails[0]);
            setSelectedListIndex(0);
            return;
        }

        setSelectedEmail({});
        setSelectedListIndex(null);
    }

    function handleNonDraftTabState(tabJustChanged) {
        if (!tabJustChanged) return;

        if (emailListTab === "inbox" && restoreInboxTabStateIfAvailable()) {
            return;
        }

        selectFirstTabEmailOrClear();
    }

    useEffect(() => {
        if (resetTabTrackingIfModalClosed()) return;

        const { tabJustChanged, prevDraftCount } = updateTabTracking();

        if (emailListTab === "drafts") {
            handleDraftsTabState(tabJustChanged, prevDraftCount);
            return;
        }

        handleNonDraftTabState(tabJustChanged);
    }, [
        emailModalOpen,
        emailListTab,
        historyDraftEmails,
        historyInboxEmails,
        historySentEmails,
        tabEmails,
        draftCount,
        isComposeNewActive,
        emailHistoryLoading,
        emailModalLoading,
    ]);

    useEffect(() => {
        if (!emailModalOpen) {
            autoComposeTriggeredRef.current = false;
            return;
        }
        if (!autoComposeIfNoDrafts) return;
        if (emailListTab !== "drafts") return;
        if (emailHistoryLoading || emailModalLoading) return;
        if (isComposeNewActive) return;
        if (draftCount > 0) {
            autoComposeTriggeredRef.current = false;
            onReplyNowAutoComposeComplete?.();
            return;
        }
        if (autoComposeTriggeredRef.current) return;

        autoComposeTriggeredRef.current = true;
    }, [
        emailModalOpen,
        autoComposeIfNoDrafts,
        emailListTab,
        emailHistoryLoading,
        emailModalLoading,
        isComposeNewActive,
        draftCount,
    ]);

    useEffect(() => {
        if (!emailModalOpen) return;
        if (!isReplyNowCompose) return;

        openBlankComposer();
    }, [emailModalOpen, isReplyNowCompose]);

    const filteredTabEmails = useMemo(
        () => filterEmailsBySearch(tabEmails, searchQuery),
        [tabEmails, searchQuery],
    );


    function clearComposerPanel() {
        draftTabStateRef.current = null;
        inboxTabStateRef.current = null;
        setInboxReplyRestore(null);
        setSelectedEmail({});
        setSelectedListIndex(null);
        setInboxReplyTrigger(0);
        setIsComposeNewActive(false);
        setIsReplyMode(false);
        setEmailTo([]);
        setEmailCc([]);
        setEmailSubject("");
        setEmailBody("");
        applyComposerMailSource(null);
        setEmailSendSuccess(false);
        setDraftSaveSuccess(false);
        autoComposeTriggeredRef.current = false;
    }

    function resetDetailPanel() {
        clearComposerPanel();
    }

    function handleCloseEmailModal() {
        if (emailModalLoading) return;
        setEmailSendSuccess(false);
        setDraftSaveSuccess(false);
        setEmailSent(false);
        setIsEmailModalExpanded(false);
        setEmailModalOpen(false);
        resetDetailPanel();
        applyComposerMailSource(null);
    }

    function handleDeleteDialogClose() {
        setDeleteConfirmOpen(false);
        setPendingDeleteEmail(null);
    }

    function handleDeleteEmailClick(email, e) {
        if (e?.stopPropagation) e.stopPropagation();
        setPendingDeleteEmail(email);
        setDeleteConfirmOpen(true);
    }

    async function handleDeleteConfirm() {
        if (!pendingDeleteEmail) {
            handleDeleteDialogClose();
            return;
        }
        const email = pendingDeleteEmail;
        const email_id = getEmailRecordId(email);

        if (email_id == null) {
            onRemoveLocalDraftEmail?.(email);
            handleDeleteDialogClose();
            setEmailSendSuccess(false);
            setDraftSaveSuccess(false);
            if ((historyDraftEmails?.length ?? 0) <= 1) {
                clearComposerPanel();
            }
            return;
        }

        setDeleteConfirmOpen(false);
        setPendingDeleteEmail(null);
        setEmailModalLoading(true);
        try {
            await emailTriageDeleteMail(email_id);
            await onRefreshEmailHistory?.();
            setEmailSendSuccess(false);
            setDraftSaveSuccess(false);
            clearComposerPanel();
        } catch (err) {
            console.error("Delete mail failed:", err);
            onActionError?.("Could not delete this email. Please try again.");
        } finally {
            setEmailModalLoading(false);
        }
    }

    async function handleDraftSave() {
        setEmailSendSuccess(false);
        setDraftSaveSuccess(false);
        setEmailModalLoading(true);
        try {
            const payload = buildMailPayload(true, selectedEmail);
            await emailTriagePostMailAction(payload);
            await onRefreshEmailHistory?.();
            setDraftSaveSuccess(true);
        } catch (err) {
            console.error("Save draft failed:", err);
            onActionError?.("Could not save draft. Please try again.");
        } finally {
            setEmailModalLoading(false);
        }
    }

    async function handleDraftSend() {
        setDraftSaveSuccess(false);
        setEmailSendSuccess(false);
        setEmailSent(true);
        setEmailModalLoading(true);
        try {
            const payload = buildMailPayload(false, selectedEmail);
            await emailTriagePostMailAction({ ...payload, row_id: selectedPO?.rowId });
            await onRefreshEmailHistory?.();
            setEmailSendSuccess(true);
        } catch (err) {
            console.error("Send email failed:", err);
            onActionError?.("Could not send email. Please try again.");
        } finally {
            setEmailSent(false);
            setEmailModalLoading(false);
        }
    }

    function openBlankComposer() {
        clearComposerPanel();
        setIsComposeNewActive(true);
    }

    useEffect(() => {
        if (!emailModalOpen) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape" && !emailModalLoading) {
                handleCloseEmailModal();
            }
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emailModalOpen, emailModalLoading]);

    const handleSentUiContinueEditing = () => {
        if (!selectedEmail) return;
        const draftRow = {
            ...selectedEmail,
            email_sub: historyListSubject(selectedEmail),
            email_body: String(
                selectedEmail.email_body ??
                selectedEmail.mail_content ??
                "",
            ),
            email_to: normalizeRecipientList(selectedEmail.email_to),
            email_cc: normalizeRecipientList(selectedEmail.email_cc),
            is_draft: true,
            is_new_mail: true,
        };
        setEmailListTab("drafts");
        setSelectedListIndex(null);
        syncComposerFromEmail(draftRow);
        setDraftSyncKey((k) => k + 1);
    };


    const handleSelectEmail = (index, email) => {
        setSelectedListIndex(index);
        setInboxReplyTrigger(0);
        setIsComposeNewActive(false);
        setEmailSendSuccess(false);
        setDraftSaveSuccess(false);
        setIsReplyMode(false);
        setInboxReplyRestore(null);
        if (emailListTab === "inbox") {
            inboxTabStateRef.current = null;
        }
        if (emailListTab === "drafts") {
            syncComposerFromEmail({ ...email, is_new_mail: true });
            setDraftSyncKey((k) => k + 1);
        } else {
            setSelectedEmail(email);
        }
    };

    const handleTabChange = (tab) => {
        if (tab === emailListTab) return;

        if (emailListTab === "drafts") {
            draftTabStateRef.current = {
                selectedEmail,
                selectedListIndex,
                isComposeNewActive,
                emailTo,
                emailCc,
                emailSubject,
                emailBody,
                mailSource: composerMailSourceRef.current,
            };
        }

        if (emailListTab === "inbox") {
            const liveReply = inboxUiApiRef.current?.getSnapshot?.() ?? {};
            inboxTabStateRef.current = {
                selectedEmail,
                selectedListIndex,
                isReplyMode,
                replyComposerEmail: liveReply.replyComposerEmail ?? {},
                isReplyContextExpanded: Boolean(liveReply.isReplyContextExpanded),
            };
        }

        setEmailListTab(tab);
        setIsEmailListCollapsed(false);
        setEmailSendSuccess(false);
        setDraftSaveSuccess(false);
    };

    const triggerInboxReply = (e, email) => {
        if (e?.stopPropagation) e.stopPropagation();
        setSelectedEmail(email);
        setSelectedListIndex(filteredTabEmails.indexOf(email));
        setInboxReplyTrigger((n) => n + 1);
    };

    function buildClientReplyTemplate(originalEmail, includeAll = false) {
        if (!originalEmail) return {};
        const origSubject = String(originalEmail.email_sub ?? originalEmail.mail_subject ?? "");
        const newSubject = origSubject.startsWith("Re: ") ? origSubject : `Re: ${origSubject}`;
        const origFrom = originalEmail.email_from ? String(originalEmail.email_from) : "";
        const origTo = normalizeRecipientList(originalEmail.email_to);
        const origCc = normalizeRecipientList(originalEmail.email_cc);
        const myEmail = (getUserEmailFromStorage() || "").toLowerCase();
        const newTo = origFrom ? [origFrom] : origTo.slice(0, 1);
        const newCc = includeAll
            ? Array.from(
                new Set(
                    [...origTo, ...origCc].filter((addr) => {
                        const a = String(addr).toLowerCase();
                        return a && a !== myEmail && a !== origFrom.toLowerCase();
                    }),
                ),
            )
            : [];
        const merged = normalizeMailActionSourceRecord(
            mergeEmailMailSourceRecords(originalEmail, {
                is_new_mail: false,
                reply_all: includeAll,
            }),
        );
        applyComposerMailSource(merged);
        return {
            email_sub: newSubject,
            email_to: newTo,
            email_cc: newCc,
            email_body: "",
            message_id: merged.message_id,
            thread_id: merged.thread_id,
        };
    }

    function mapProcessMailResponseToComposerTemplate(data, { isNewMail = false } = {}) {
        if (!data || typeof data !== "object") return null;
        applyComposerMailSource(
            normalizeMailActionSourceRecord({
                ...data,
                is_new_mail: isNewMail,
            }),
        );
        return {
            email_to: normalizeRecipientList(data.email_to),
            email_cc: normalizeRecipientList(data.email_cc),
            email_sub: data.email_sub ?? data.mail_subject ?? "",
            email_body: resolveEmailBodyFromApi(data),
            message_id: data.message_id ?? data.messageId ?? null,
            thread_id: data.thread_id ?? data.threadId ?? null,
        };
    }

    function resolveProcessMailItems() {
        const vendorName = String(selectedPO?.vendorName ?? "").trim();
        const rows = Array.isArray(selectedDispatchRows) ? selectedDispatchRows : [];
        if (rows.length > 0) {
            return buildProcessMailV2ItemsFromRows(rows, vendorName);
        }
        return buildProcessMailV2ItemsSnapshot({
            vendorScopeName: vendorName,
            vendorGrouped: false,
            selectedDispatchRowKeys: null,
            dispatchRows: [],
            fallbackPo: selectedPO?.poNumber,
            fallbackMaterialId: selectedPO?.materialId,
            fallbackId: selectedPO?.rowId,
        });
    }

    async function handlePrepareInboxReply(originalEmail, includeAll = false) {
        if (!originalEmail) return {};
        const vendorName = String(selectedPO?.vendorName ?? "").trim();
        const items = resolveProcessMailItems();

        if (!items.length) {
            console.warn("process_mail/v2: empty items — using client reply stub");
            return buildClientReplyTemplate(originalEmail, includeAll);
        }

        const userName = getUserNameFromStorage();
        const sub =
            String(originalEmail.email_sub ?? originalEmail.mail_subject ?? "").trim() ||
            (vendorName ? `Supply Dispatch Summary - ${vendorName}` : "");
        const rawMid = originalEmail.message_id ?? originalEmail.messageId;
        const message_id =
            rawMid != null && String(rawMid).trim() !== "" ? String(rawMid).trim() : "";

        setEmailModalLoading(true);
        try {
            const data = await emailTriagePostProcessMailV2({
                vendor_name: vendorName,
                ...(userName ? { user_name: userName } : {}),
                email_sub: sub,
                items,
                message_id,
                reply_source: "inbox",
            });
            const template = mapProcessMailResponseToComposerTemplate(data);
            if (template) {
                applyComposerMailSource(
                    normalizeMailActionSourceRecord({
                        ...composerMailSourceRef.current,
                        reply_all: includeAll,
                    }),
                );
                return template;
            }
            return buildClientReplyTemplate(originalEmail, includeAll);
        } catch (err) {
            console.error("process_mail/v2 reply failed:", err);
            onActionError?.("Could not load reply template. Using a basic reply draft.");
            return buildClientReplyTemplate(originalEmail, includeAll);
        } finally {
            setEmailModalLoading(false);
        }
    }

    async function handleComposeNewEmail({ fromReplyNowAuto = false } = {}) {
        const vendorName = String(selectedPO?.vendorName ?? "").trim();
        const items = resolveProcessMailItems();

        if (!items.length) {
            autoComposeTriggeredRef.current = false;
            onActionError?.("Cannot compose email: PO number and material code are required.");
            if (fromReplyNowAuto) onReplyNowAutoComposeComplete?.();
            return;
        }

        setEmailSendSuccess(false);
        setDraftSaveSuccess(false);
        setInboxReplyTrigger(0);
        setEmailModalLoading(true);
        try {
            const userName = getUserNameFromStorage();
            const data = await emailTriagePostProcessMailV2({
                vendor_name: vendorName,
                items,
                ...(userName ? { user_name: userName } : {}),
            });

            const template = mapProcessMailResponseToComposerTemplate(data, { isNewMail: true });
            if (!template) {
                autoComposeTriggeredRef.current = false;
                onActionError?.("Could not load email draft. Please try again.");
                return;
            }

            syncComposerFromEmail({
                ...(data && typeof data === "object" ? data : {}),
                email_to: template.email_to,
                email_cc: template.email_cc,
                email_sub: template.email_sub,
                email_body: template.email_body,
                is_draft: true,
                is_new_mail: true,
            });
            setIsComposeNewActive(true);
            setSelectedListIndex(null);
            setEmailListTab("drafts");
            setDraftSyncKey((k) => k + 1);
            await onRefreshEmailHistory?.();
        } catch (err) {
            autoComposeTriggeredRef.current = false;
            console.error("process_mail/v2 compose failed:", err);
            onActionError?.("Could not compose new email. Please try again.");
        } finally {
            setEmailModalLoading(false);
            if (fromReplyNowAuto) onReplyNowAutoComposeComplete?.();
        }
    }

    async function handleInboxSaveDraft(composerData, sourceEmail) {
        setEmailSendSuccess(false);
        setDraftSaveSuccess(false);
        setEmailModalLoading(true);
        try {
            await emailTriagePostMailAction(
                buildMailPayload(true, sourceEmail, composerData),
            );
            await onRefreshEmailHistory?.();
            setDraftSaveSuccess(true);
        } catch (err) {
            console.error("Save inbox reply draft failed:", err);
            onActionError?.("Could not save draft. Please try again.");
        } finally {
            setEmailModalLoading(false);
        }
    }

    async function handleInboxSendEmail(composerData, sourceEmail) {
        setDraftSaveSuccess(false);
        setEmailSendSuccess(false);
        setEmailSent(true);
        setEmailModalLoading(true);
        try {
            await emailTriagePostMailAction(
                buildMailPayload(false, sourceEmail, composerData),
            );
            await onRefreshEmailHistory?.();
            setEmailSendSuccess(true);
        } catch (err) {
            console.error("Send inbox reply failed:", err);
            onActionError?.("Could not send email. Please try again.");
        } finally {
            setEmailSent(false);
            setEmailModalLoading(false);
        }
    }

    const hasSelectedEmail = selectedEmail && Object.keys(selectedEmail).length > 0;

    function getEmptyStateConfig() {
        if (searchQuery) {
            let EmptyIcon = ArrowUpRight;

            if (emailListTab === "inbox") {
                EmptyIcon = Inbox;
            } else if (emailListTab === "drafts") {
                EmptyIcon = PenLine;
            }

            return {
                EmptyIcon,
                emptyTitle: "No emails found",
                emptyText: "Try adjusting your search",
            };
        }

        const configByTab = {
            inbox: {
                EmptyIcon: Inbox,
                emptyTitle: "No Message Received",
                emptyText: "Incoming emails for this vendor will appear here",
            },
            sent: {
                EmptyIcon: ArrowUpRight,
                emptyTitle: "No sent emails",
                emptyText: "Previously sent emails for this PO will appear here",
            },
            drafts: {
                EmptyIcon: PenLine,
                emptyTitle: "No drafts saved",
                emptyText: "Saved drafts for this PO will appear here",
            },
        };

        return configByTab[emailListTab] ?? configByTab.drafts;
    }

    function renderEmptyEmailList() {
        const { EmptyIcon, emptyTitle, emptyText } = getEmptyStateConfig();

        return (
            <div className="empty-state">
                <div className="empty-circle">
                    <EmptyIcon size={20} className="empty-icon" />
                </div>
                <h3 className="empty-title">{emptyTitle}</h3>
                <p className="empty-text">{emptyText}</p>
            </div>
        );
    }

    function getEmailRowIcon() {
        const iconByTab = {
            drafts: <PenLine size={14} aria-hidden />,
            sent: <ArrowUpRight size={14} aria-hidden />,
            inbox: <ArrowDownLeft size={14} aria-hidden />,
        };

        return iconByTab[emailListTab] ?? iconByTab.inbox;
    }

    function getEmailIconBoxModifier() {
        const modifierByTab = {
            drafts: " email-item-icon-box--draft",
            sent: " email-item-icon-box--sent",
            inbox: " email-item-icon-box--inbox",
        };

        return modifierByTab[emailListTab] ?? modifierByTab.inbox;
    }

    function renderInboxRowAction(email) {
        if (emailListTab !== "inbox") return null;

        return (
            <button
                type="button"
                onClick={(e) => triggerInboxReply(e, email)}
                disabled={emailModalLoading}
                className="email-item-action-btn email-item-action-btn--reply"
                title="Reply"
                aria-label="Reply"
            >
                <Reply size={14} strokeWidth={2.25} />
            </button>
        );
    }

    function renderDraftRowAction(email) {
        if (emailListTab !== "drafts") return null;

        return (
            <button
                type="button"
                onClick={(e) => handleDeleteEmailClick(email, e)}
                disabled={emailModalLoading}
                className="email-item-action-btn email-item-action-btn--delete"
                title="Delete email"
                aria-label="Delete email"
            >
                <Trash2 size={12} strokeWidth={2.25} />
            </button>
        );
    }

    function renderEmailRow(email, index) {
        const isSelected = selectedListIndex === index;

        return (
            <div
                key={`${email.email_id ?? email.message_id ?? index}-${index}`}
                className={`email-list-item ${isSelected ? "selected" : ""}`}
            >
                <button
                    type="button"
                    onClick={() => handleSelectEmail(index, email)}
                    className="email-item-btn"
                >
                    <div className="email-item-grid">
                        <span
                            className={`email-item-icon-box${getEmailIconBoxModifier()}`}
                            aria-hidden
                        >
                            {getEmailRowIcon()}
                        </span>

                        <div className="email-item-body">
                            <div className="email-item-row">
                                <div className="email-subject">
                                    {email.email_sub || email.mail_subject || ""}
                                </div>
                            </div>
                            <div className="email-to-address">
                                {email.email_to}
                            </div>
                            <div className="email-time">
                                {formatEmailTimestampToIST(email.timestamp)}
                            </div>
                        </div>
                    </div>
                </button>

                <fieldset
                    className="email-item-actions"
                    aria-label="Email actions"
                >
                    {renderInboxRowAction(email)}
                    {renderDraftRowAction(email)}
                </fieldset>
            </div>
        );
    }

    function renderEmailListContent() {
        if (filteredTabEmails.length === 0) {
            return renderEmptyEmailList();
        }

        return (
            <div className="email-list-rows">
                {filteredTabEmails.map(renderEmailRow)}
            </div>
        );
    }

    function renderSuccessPanel(title, text) {
        return (
            <div className="email-send-success-panel">
                <div className="email-send-success-icon-wrap">
                    <CheckCircle2 size={32} color="#16a34a" strokeWidth={2} />
                </div>
                <h3 className="email-send-success-title">
                    {title}
                </h3>
                <p className="email-send-success-text">
                    {text}
                </p>
            </div>
        );
    }

    function renderMailSentSuccess() {
        return renderSuccessPanel(
            "Mail sent successfully",
            <>
                You can compose a new mail by clicking{" "}
                <strong>Compose New Email</strong> in the header.
            </>,
        );
    }

    function renderDraftSavedSuccess() {
        return renderSuccessPanel(
            "Draft saved successfully",
            <>
                Continue editing by selecting this draft on the left, or start another message with{" "}
                <strong>Compose New Email</strong>.
            </>,
        );
    }

    function renderDraftComposer() {
        const shouldShowDraftComposer =
            isComposeNewActive || (draftCount > 0 && hasSelectedEmail);

        if (!shouldShowDraftComposer) {
            return (
                <output className="composer-empty-panel">
                    <span className="composer-empty-panel-icon" aria-hidden>
                        <PenLine size={28} strokeWidth={2} />
                    </span>
                    <span className="composer-empty-panel-text">
                        No saved draft present. Compose a new email.
                    </span>
                </output>
            );
        }

        return (
            <div className="composer-outlook-sheet">
                <div className="reply-compose-shell__header">
                    <div className="reply-compose-shell__title-wrap">
                        <span className="reply-compose-shell__icon" aria-hidden>
                            <Pen size={14} />
                        </span>
                        <div
                            style={{
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                maxWidth: "100%",
                            }}
                        >
                            <h3 className="reply-compose-shell__title">
                                {emailSubject || (isComposeNewActive ? "New email" : "Draft")}
                            </h3>
                        </div>
                    </div>

                    <div className="reply-compose-shell__actions">
                        <button
                            type="button"
                            onClick={() => void handleDraftSave()}
                            disabled={emailModalLoading}
                            className="reply-compose-shell__btn reply-compose-shell__btn--draft"
                        >
                            Save Draft
                        </button>
                        <button
                            type="button"
                            onClick={() => void handleDraftSend()}
                            disabled={emailSent || emailModalLoading}
                            className="reply-compose-shell__btn reply-compose-shell__btn--send"
                        >
                            {emailSent ? "Sending..." : "Send Email"}
                        </button>
                    </div>
                </div>

                <EmailDraftUI
                    key={draftSyncKey}
                    emailTo={emailTo}
                    emailCc={emailCc}
                    emailSubject={emailSubject}
                    setEmailTo={setEmailTo}
                    setEmailCc={setEmailCc}
                    setEmailSubject={setEmailSubject}
                    emailFrom={emailFrom}
                    emailBody={emailBody}
                    setEmailBody={setEmailBody}
                    composerReadOnly={false}
                />
            </div>
        );
    }

    function renderSentComposer() {
        if (!hasSelectedEmail) return null;

        return (
            <div className="composer-outlook-sheet">
                <EmailSentUI
                    value={selectedEmail.email_body ?? selectedEmail.mail_content}
                    selectedEmail={selectedEmail}
                    onContinueEditing={handleSentUiContinueEditing}
                />
            </div>
        );
    }

    function renderInboxComposer() {
        if (!hasSelectedEmail) return null;

        return (
            <div className="composer-outlook-sheet">
                <EmailInboxUI
                    isReplyMode={isReplyMode}
                    setIsReplyMode={setIsReplyMode}
                    selectedEmail={selectedEmail}
                    emailFrom={emailFrom}
                    emailSent={emailSent}
                    emailModalLoading={emailModalLoading}
                    onSaveDraft={handleInboxSaveDraft}
                    onSendEmail={handleInboxSendEmail}
                    onPrepareReply={handlePrepareInboxReply}
                    replyTrigger={inboxReplyTrigger}
                    snapshotApiRef={inboxUiApiRef}
                    restoredReply={inboxReplyRestore}
                    onRestoredReplyApplied={() => setInboxReplyRestore(null)}
                />
            </div>
        );
    }

    function getComposerEmptyIcon() {
        const iconByTab = {
            inbox: <Inbox size={28} strokeWidth={2} />,
            sent: <Send size={28} strokeWidth={2} />,
            drafts: <PenLine size={28} strokeWidth={2} />,
        };

        return iconByTab[emailListTab] ?? iconByTab.drafts;
    }

    function renderEmptyComposerPanel() {
        return (
            <output className="composer-empty-panel">
                <span className="composer-empty-panel-icon" aria-hidden>
                    {getComposerEmptyIcon()}
                </span>

                <strong className="composer-empty-panel-title">
                    Select an email
                </strong>

                <span className="composer-empty-panel-text">
                    Choose a message from the list on the left to view or compose.
                </span>
            </output>
        );
    }

    function renderComposerContent() {
        if (emailSendSuccess) return renderMailSentSuccess();
        if (draftSaveSuccess) return renderDraftSavedSuccess();
        if (emailListTab === "drafts") return renderDraftComposer();
        if (emailListTab === "sent") return renderSentComposer() ?? renderEmptyComposerPanel();
        if (emailListTab === "inbox") return renderInboxComposer() ?? renderEmptyComposerPanel();

        return renderEmptyComposerPanel();
    }

    return (
        <>
            {emailModalOpen && selectedPO && (
                <>
                    <DraftEmailApiLoader active={emailModalLoading || emailHistoryLoading} />
                    <>
                        <div className="modal-backdrop" aria-hidden />

                        <dialog
                            className={`email-center-modal${isEmailModalExpanded ? ' email-center-modal--expanded' : ''
                                }`}
                            open
                            aria-labelledby="email-center-title"
                            onCancel={handleCloseEmailModal}
                        >
                            <div className="email-center-header">
                                <div className="header-left">
                                    <div className="header-icon-box" aria-hidden>
                                        <Mail size={17} className="header-icon" />
                                    </div>

                                    <div className="header-text">
                                        <p className="header-eyebrow">Email Center</p>
                                        <h3 id="email-center-title" className="header-vendor-name">
                                            {selectedPO?.vendorName || 'Vendor'}
                                        </h3>
                                    </div>
                                </div>

                                <div className="header-actions">
                                    <button
                                        type="button"
                                        onClick={() => void handleComposeNewEmail()}
                                        disabled={emailModalLoading}
                                        className="header-compose-btn"
                                        title="Compose new email"
                                    >
                                        <PenLine size={12} aria-hidden />{' '}
                                        Compose New Email
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setIsEmailModalExpanded((prev) => !prev)}
                                        className="header-expand-btn"
                                        title={
                                            isEmailModalExpanded
                                                ? 'Collapse email center'
                                                : 'Expand email center'
                                        }
                                        aria-label={
                                            isEmailModalExpanded
                                                ? 'Collapse email center'
                                                : 'Expand email center'
                                        }
                                    >
                                        <Expand size={12} aria-hidden />
                                    </button>

                                    <span className="header-actions-divider" aria-hidden />

                                    <button
                                        type="button"
                                        onClick={handleCloseEmailModal}
                                        disabled={emailModalLoading}
                                        className="header-close-btn"
                                        aria-label="Close email modal"
                                    >
                                        <X size={18} aria-hidden />
                                    </button>
                                </div>
                            </div>

                            <div className="email-center-layout">
                                <div
                                    className={`email-list-panel${isEmailListCollapsed ? ' email-list-panel--collapsed' : ''
                                        }`}
                                >
                                    <div className="email-list-collapsed-rail">
                                        <button
                                            type="button"
                                            className="email-list-rail-expand"
                                            onClick={() => setIsEmailListCollapsed(false)}
                                            title="Expand email list"
                                            aria-label="Expand email list"
                                        >
                                            <ChevronRight size={16} aria-hidden />
                                        </button>

                                        <div className="email-list-rail-tabs">
                                            <button
                                                type="button"
                                                className={`email-list-rail-tab${emailListTab === 'inbox'
                                                        ? ' email-list-rail-tab--active'
                                                        : ''
                                                    }`}
                                                onClick={() => handleTabChange('inbox')}
                                                title="Inbox"
                                                aria-label="Inbox"
                                            >
                                                <Inbox size={15} aria-hidden />
                                            </button>

                                            <button
                                                type="button"
                                                className={`email-list-rail-tab${emailListTab === 'sent'
                                                        ? ' email-list-rail-tab--active'
                                                        : ''
                                                    }`}
                                                onClick={() => handleTabChange('sent')}
                                                title="Sent"
                                                aria-label="Sent"
                                            >
                                                <Send size={15} aria-hidden />
                                            </button>

                                            <button
                                                type="button"
                                                className={`email-list-rail-tab${emailListTab === 'drafts'
                                                        ? ' email-list-rail-tab--active'
                                                        : ''
                                                    }`}
                                                onClick={() => handleTabChange('drafts')}
                                                title="Drafts"
                                                aria-label="Drafts"
                                            >
                                                <Mail size={15} aria-hidden />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="email-list-top">
                                        <button
                                            type="button"
                                            className="email-list-collapse-btn"
                                            onClick={() => setIsEmailListCollapsed(true)}
                                            title="Collapse email list"
                                            aria-label="Collapse email list"
                                        >
                                            <ChevronLeft size={16} aria-hidden />
                                        </button>

                                        <div className="search-container">
                                            <div className="search-wrapper">
                                                <Search size={12} className="search-icon" aria-hidden />
                                                <input
                                                    type="search"
                                                    aria-label="Search emails"
                                                    placeholder="Search emails..."
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                    className="search-input"
                                                />
                                            </div>
                                        </div>

                                        <div className="email-list-tabs">
                                            <div className="tabs-container">
                                                <button
                                                    type="button"
                                                    onClick={() => handleTabChange('inbox')}
                                                    className={`tab-button ${emailListTab === 'inbox' ? 'active-inbox' : ''
                                                        }`}
                                                >
                                                    <Inbox size={13} aria-hidden />{' '}
                                                    Inbox
                                                </button>

                                                <button
                                                    type="button"
                                                    onClick={() => handleTabChange('sent')}
                                                    className={`tab-button ${emailListTab === 'sent' ? 'active-sent' : ''
                                                        }`}
                                                >
                                                    <ArrowUpRight size={13} aria-hidden />{' '}
                                                    Sent
                                                </button>

                                                <button
                                                    type="button"
                                                    onClick={() => handleTabChange('drafts')}
                                                    className={`tab-button ${emailListTab === 'drafts' ? 'active-draft' : ''
                                                        }`}
                                                >
                                                    <PenLine size={13} aria-hidden />{' '}
                                                    Drafts
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="email-list-scroll">
                                        {renderEmailListContent()}
                                    </div>

                                    <div className="email-list-footer">
                                        <div className="footer-stats">
                                            <span className="footer-stat">
                                                <span className="footer-dot footer-dot--green" aria-hidden />
                                                <span>{historyInboxEmails?.length ?? 0}</span>{' '}
                                                received
                                            </span>

                                            <span className="footer-stat">
                                                <span className="footer-dot footer-dot--blue" aria-hidden />
                                                <span>{historySentEmails?.length ?? 0}</span>{' '}
                                                sent
                                            </span>

                                            <span className="footer-stat">
                                                <span className="footer-dot footer-dot--amber" aria-hidden />
                                                <span>{historyDraftEmails?.length ?? 0}</span>{' '}
                                                drafts
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="email-detail-panel">
                                    <div
                                        className={`composer-body${!emailSendSuccess && !draftSaveSuccess
                                                ? ' composer-body--outlook'
                                                : ''
                                            }`}
                                    >
                                        {renderComposerContent()}
                                    </div>
                                </div>
                            </div>
                        </dialog>
                    </>
                    <DeleteConfirmationModel
                        deleteConfirmOpen={deleteConfirmOpen}
                        handleDeleteDialogClose={handleDeleteDialogClose}
                        handleDeleteConfirm={() => void handleDeleteConfirm()}
                    />
                </>
            )}
        </>
    );
}

DraftEmail.propTypes = {
  emailModalOpen: PropTypes.bool.isRequired,
  isReplyNowCompose: PropTypes.bool,
  setEmailModalOpen: PropTypes.func.isRequired,

  historyDraftEmails: PropTypes.arrayOf(PropTypes.object),
  historyInboxEmails: PropTypes.arrayOf(PropTypes.object),
  historySentEmails: PropTypes.arrayOf(PropTypes.object),

  emailListTab: PropTypes.oneOf([
    "inbox",
    "sent",
    "drafts",
  ]).isRequired,
  setEmailListTab: PropTypes.func.isRequired,

  selectedPO: PropTypes.shape({
    rowId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    vendorName: PropTypes.string,
    poNumber: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    materialId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }),

  onRefreshEmailHistory: PropTypes.func,
  onRemoveLocalDraftEmail: PropTypes.func,
  onActionError: PropTypes.func,

  emailHistoryLoading: PropTypes.bool,

  autoComposeIfNoDrafts: PropTypes.bool,
  onReplyNowAutoComposeComplete: PropTypes.func,

  selectedDispatchRows: PropTypes.array,
  vendorGrouped: PropTypes.bool,
};

